import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { TerminationTypeFormRule, TerminationTypeFormSchema } from "@core/form/form.rule";
import { useFormDefaultTerminationType } from "@core/form/form-default-termination-type.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateTerminationType, 
  useQyGetTerminationTypeById, 
  useQyDeleteTerminationType 
} from "@core/query/termination-type.query";
import { GetResponseTerminationTypeModel } from "@core/model/query-model-termination-type";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditTerminationType() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultTerminationType();
  const navigate = useNavigate();
  const { mapUpdateTerminationType, mapDeleteTerminationType } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { terminationTypeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID TerminationType
  const handleGetApiSuccess = (data: GetResponseTerminationTypeModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: terminationTypeResponse,
    isLoading,
    isError: terminationTypeError,
  } = useQyGetTerminationTypeById(terminationTypeId || "", handleGetApiSuccess);
  const terminationTypeData = terminationTypeResponse?.data?.[0];

  // API UPDATE TerminationType
  const handleUpdateApiSuccess = () => {
    showSuccess("TerminationType updated successfully");
  };
  const { mutate: updateTerminationType, isLoading: isEditLoading } = useQyUpdateTerminationType(
    handleUpdateApiSuccess
  );

  // API DELETE TerminationType
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("TerminationType deleted successfully");
  };
  const { mutate: deleteTerminationType } = useQyDeleteTerminationType(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<TerminationTypeFormSchema>({
    defaultValues: getDefault(terminationTypeData),
    resolver: zodResolver(TerminationTypeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TerminationTypeFormSchema) => {
    if (!terminationTypeData) {
      throw new Error("No terminationType data");
    }

    const formData = mapUpdateTerminationType(form, terminationTypeData.code);
    updateTerminationType(formData);
  };
  const handleValidateError = (err: FieldErrors<TerminationTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!terminationTypeData) {
      throw new Error("No terminationType data found");
    }

    const form = mapDeleteTerminationType(terminationTypeData.code);
    deleteTerminationType(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    terminationTypeId,
    terminationTypeData,
    isLoading,
    terminationTypeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}